import { FormArray, FormControl, Validators } from '@angular/forms';
import { OiqFormBase } from '@oiq-ui-sdk/form';
import { normalizeSsn } from '@shared/utils';
import {
  OiqCreditCheckArray,
  OiqCreditCheckForm,
} from '@v2shared/components/credit-check/credit-check.form';
import { OiqSsnLookup } from '@v2shared/components/ssn-alert/ssn-alert.models';

import {
  ApplicationService,
  AttachmentService,
  ClientService,
  ContactService,
  OcrService,
} from '@core/services';
import { catchError, distinctUntilChanged, forkJoin, map, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';

export class OiqApplicationAddForm extends OiqFormBase {
  public attachments: number[] = [];

  public clientId$ = this.get('clientId').valueChanges.pipe(distinctUntilChanged());
  public whiteLabelId$ = this.get('whiteLabelId').valueChanges.pipe(distinctUntilChanged());
  public isoId$ = this.get('isoId').valueChanges.pipe(distinctUntilChanged());
  public pendingOverrideData: {
    clientId?: number;
    isoId?: number;
    isoSalesRepresentativeId?: number;
  } = null;
  public pendingCreditCheck: any = null;
  get emailRecipients(): FormArray<FormControl<string>> {
    return this.get('additionalEmailRecipients') as FormArray<FormControl<string>>;
  }

  constructor(
    private _applications: ApplicationService,
    private _contacts: ContactService,
    private _attachments: AttachmentService,
    private _ocr: OcrService,
    private _clients: ClientService
  ) {
    super({
      id: new FormControl(null),
      clientId: new FormControl('', [Validators.required]),
      clientName: new FormControl('', [Validators.required]),
      source: new FormControl('Internal'),
      operationsNotes: new FormControl(''),
      isoId: new FormControl('', [Validators.required]),
      whiteLabelId: new FormControl('', [Validators.required]),
      isoSalesRepresentativeId: new FormControl(''),
      isoRecipients: new FormControl([]),
      type: new FormControl('New_Deal', [Validators.required]),
      originalApplicationId: new FormControl(''),
      sendEmailNotifications: new FormControl(true),
      sendSmsNotifications: new FormControl(false),
      // decisionLogicRequest: new ApplicationDecisionLogicForm(),
      attachmentIds: new FormControl<number[]>(null),
      ocrIds: new FormControl<number[]>(null),
      urgent: new FormControl(false),
      creditCheckRequests: new OiqCreditCheckArray(),
      additionalEmailRecipients: new FormArray<FormControl<string[]>>([], []),
    });

    this.controls.isoSalesRepresentativeId.disable();
    this.controls.whiteLabelId.valueChanges.subscribe(whiteLabelId => {
      if (this.controls.whiteLabelId.pristine) {
        return;
      }
      this.resetClientData();
    });

    this.isoId$.subscribe(isoId => {
      !isoId
        ? this.controls.isoSalesRepresentativeId.disable()
        : this.controls.isoSalesRepresentativeId.enable();

      this.controls.isoSalesRepresentativeId.setValue(null);
    });
  }

  // get decisionLogicForm(): ApplicationDecisionLogicForm {
  //   return this.controls.decisionLogicRequest as ApplicationDecisionLogicForm;
  // }

  get creditCheckRequests(): OiqCreditCheckArray {
    return this.controls.creditCheckRequests as OiqCreditCheckArray;
  }

  // entered owner SSNs, matched against other clients than the selected one
  get ssnLookup(): OiqSsnLookup {
    const _clientId = this.get('clientId').value;
    return {
      owners: this.creditCheckRequests.controls
        .map(item => ({
          ssn: normalizeSsn(item.get('ssn')?.value),
          name: [item.get('firstName')?.value, item.get('lastName')?.value]
            .filter(Boolean)
            .join(' '),
        }))
        .filter(owner => !!owner.ssn),
      excludeClientId: _clientId,
      alertScope: `client:${_clientId}`,
    };
  }

  private _client: any = null;
  private _whiteLabels: any[] = [];
  private _defaultWhiteLabel: any = null;

  get client() {
    return this._client;
  }

  set client(value) {
    this._client = value;

    if (!this._client) {
      this.resetClientData();
      this.creditCheckRequests.clear();
      return;
    }

    const _matchingIsoId = this.pendingOverrideData?.isoId;
    const _matchingSalesRepId = this.pendingOverrideData?.isoSalesRepresentativeId;
    const _clientIsoId = this._client?.iso?.id;
    const _clientSalesRepId = this._client.isoSalesRepresentativeId;

    let _finalIsoId = _matchingIsoId;
    let _finalSalesRepId = _matchingSalesRepId;

    // fallback to client data
    if (!_finalIsoId) {
      _finalIsoId = _clientIsoId;
      _finalSalesRepId = _clientSalesRepId;
    }

    // fallback to client data if isoId is not set and iso's match
    if (_finalIsoId == _clientIsoId && !_finalSalesRepId) {
      _finalSalesRepId = _clientSalesRepId;
    }

    this.patchValue({
      clientId: this._client.id, // it triggers loop on change
      clientName: this._client.name,
      isoId: _finalIsoId,
    });

    this.patchValue({
      isoSalesRepresentativeId: _finalSalesRepId,
    });

    this.pendingOverrideData = null;

    this.creditCheckRequests.setOwners(this._client.owners || [], this.pendingCreditCheck);

    if (this.creditCheckRequests.invalid) {
      this.creditCheckRequests.markAllAsTouched();
      if (this.cdr) {
        this.cdr.markForCheck();
      }
    }

    if (this.pendingCreditCheck) {
      this.pendingCreditCheck = null;
    }
  }

  get whiteLabels() {
    return this._whiteLabels;
  }

  set whiteLabels(value) {
    this._whiteLabels = value;
    this._defaultWhiteLabel = (value || []).find(item => item.tenantDefault === true);
    // set default white label
    if (this._defaultWhiteLabel && !this.controls.whiteLabelId.value) {
      this.controls.whiteLabelId.setValue(this._defaultWhiteLabel.id);
      return;
    }
    // set first white label
    if (!this._defaultWhiteLabel && !this.controls.whiteLabelId.value) {
      this.controls.whiteLabelId.setValue(this._whiteLabels[0]?.id);
      return;
    }
  }

  get defaultWhiteLabel() {
    return this._defaultWhiteLabel;
  }

  get whiteLabelId() {
    return this.controls.whiteLabelId.value;
  }

  private _excludeFromCreditCheck: boolean = true;
  get excludeFromCreditCheck(): any {
    return this._excludeFromCreditCheck;
  }

  set excludeFromCreditCheck(value: boolean) {
    this._excludeFromCreditCheck = value || false;
    this.creditCheckRequests.hasFeature = this.hasCreditReport;
    this.creditCheckRequests.excludeFromCreditCheck = this._excludeFromCreditCheck;
  }

  private _features: any;
  get features(): any {
    return this._features;
  }
  set features(value: any) {
    this._features = value || {};
    this.creditCheckRequests.hasFeature = this.hasCreditReport;
  }

  get hasCreditReport(): boolean {
    return !!this._features?.['Experian'] || !!this._features?.['Crs'];
  }

  get isSendToPreUnderwriting(): boolean {
    return !!this._features?.['PreUnderwriting'];
  }

  resetClientData(): void {
    this.patchValue({
      clientId: null,
      clientName: null,
      isoId: null,
      isoSalesRepresentativeId: null,
    });
  }

  /**
   * Prepare payload for API
   */
  override get toPayload(): any {
    const payload = {
      ...this.value,
      creditCheckRequests: this.creditCheckRequests.toPayload,
    };

    delete payload['clientName'];
    delete payload['ocrIds'];

    return payload;
  }

  override requestCreate(): any {
    const _payload = this.toPayload;

    const creditCheckRequests: OiqCreditCheckForm[] = this.creditCheckRequests.controls;
    const excludedRequests = creditCheckRequests.filter(row => row.isExcluded);
    const includedRequests = creditCheckRequests.filter(row => !row.isExcluded);

    _payload.creditCheckRequests = includedRequests.map(request => request.toPayload);

    const _ocrIds = this.value.ocrIds || [];

    return this._applications.create(_payload).pipe(
      tap(_application => {
        this.id = _application.id;
      }),
      // save excluded credit checks
      switchMap(_application =>
        excludedRequests.length == 0
          ? of(_application)
          : forkJoin(
              excludedRequests.map(request => {
                const _requestPayload = request.getAddressPayload(this.client);
                return this._contacts.update(_requestPayload.id, _requestPayload).pipe(
                  catchError(_reason => {
                    console.error(
                      `Failed to update Contact request ${_requestPayload.id}:`,
                      _requestPayload,
                      _reason
                    );
                    return of(null);
                  })
                );
              })
            ).pipe(map(() => _application))
      ),
      // save ocr
      switchMap(_application =>
        _ocrIds.length == 0
          ? of(_application)
          : forkJoin(
              _ocrIds.map(ocrId =>
                this._ocr.updateOcrApplicationId(ocrId, _application.id).pipe(
                  catchError(_reason => {
                    console.error(
                      `Failed to update OCR request ${ocrId} with application ID ${_application.id}:`,
                      _reason
                    );
                    return of(null);
                  })
                )
              )
            ).pipe(map(() => _application))
      )
    );
  }

  /**
   * Set the primary owner for the application with optimistic updates and error handling.
   */
  setPrimaryOwner(ownerToUpdate: OiqCreditCheckForm) {
    const previousState = this.creditCheckRequests.setPrimaryOwner(ownerToUpdate);

    const contactId = ownerToUpdate.get('contactId').value;
    const clientId = this.get('clientId').value;

    this._clients.setPrimaryOwner(clientId, contactId).subscribe({
      next: () => {},
      error: error => {
        this.creditCheckRequests.restorePrimaryOwner(previousState);
      },
    });
  }

  fromApplicationAndClient(_type: string, _application: any, _client: any, _whiteLabelId: number) {
    this.resetClientData();

    this.pendingOverrideData = {};

    if (_whiteLabelId) {
      this.get('whiteLabelId').setValue(_whiteLabelId);
    }

    if (_client) {
      this.get('clientId').setValue(_client.id);
      this.get('clientName').setValue(_client.name);
    }

    this.get('originalApplicationId').setValue(_application?.id);

    const _isoId = _application?.iso?.id || _client?.iso?.id || null;
    const _isoSalesRepresentativeId =
      _application?.isoSalesRepresentativeId || _client?.isoSalesRepresentativeId || null;

    if (_isoId) {
      this.get('isoId').setValue(_isoId);
      this.get('isoSalesRepresentativeId').setValue(null);
      this.pendingOverrideData.isoId = _isoId;
    }

    if (_isoSalesRepresentativeId) {
      this.get('isoSalesRepresentativeId').setValue(_isoSalesRepresentativeId);
      this.pendingOverrideData.isoSalesRepresentativeId = _isoSalesRepresentativeId;
    }

    const actionToTypeMap: Record<string, string> = {
      new: 'New_Deal',
      decline: 'New_Deal',
      renew: 'Renewal',
      resubmit: 'Resubmission',
      renewResubmit: 'Renewal_Resubmission',
    };

    const typeValue = actionToTypeMap[_type];

    if (typeValue) {
      this.get('type').setValue(typeValue);
    }
  }

  fromFundingAndClient(_type: string, _funding: any, _client: any, _whiteLabelId: number) {
    this.resetClientData();

    this.pendingOverrideData = {};

    if (_whiteLabelId) {
      this.get('whiteLabelId').setValue(_whiteLabelId);
    }

    if (_client) {
      this.get('clientId').setValue(_client.id);
      this.get('clientName').setValue(_client.name);
    }

    this.get('originalApplicationId').setValue(_funding?.applicationId);

    const _isoId = _funding?.iso?.id || _client?.iso?.id || null;
    const _isoSalesRepresentativeId =
      _funding?.isoSalesRepresentativeId || _client?.isoSalesRepresentativeId || null;

    if (_isoId) {
      this.get('isoId').setValue(_isoId);
      this.get('isoSalesRepresentativeId').setValue(null);
      this.pendingOverrideData.isoId = _isoId;
    }

    if (_isoSalesRepresentativeId) {
      this.get('isoSalesRepresentativeId').setValue(_isoSalesRepresentativeId);
      this.pendingOverrideData.isoSalesRepresentativeId = _isoSalesRepresentativeId;
    }

    const actionToTypeMap: Record<string, string> = {
      new: 'New_Deal',
      decline: 'New_Deal',
      renew: 'Renewal',
      resubmit: 'Resubmission',
      renewResubmit: 'Renewal_Resubmission',
    };

    const typeValue = actionToTypeMap[_type];
    if (typeValue) {
      this.get('type').setValue(typeValue);
    }
  }

  fromMatchClient(result: any): void {
    if (!result) return;

    this.pendingCreditCheck = result.creditCheck;

    if (result.funding) {
      this.fromFundingAndClient(
        result.type || result.action || '',
        result.funding || {},
        result.client || result.selectedClient || {},
        result.whiteLabelId || this.controls.whiteLabelId.value
      );
      return;
    }

    this.fromApplicationAndClient(
      result.type || result.action || '',
      result.application || {},
      result.client || result.selectedClient || {},
      result.whiteLabelId || this.controls.whiteLabelId.value
    );
  }

  fromSearchClient(result: any): void {
    if (!result) return;

    this.pendingCreditCheck = null;

    if (result.funding) {
      this.fromFundingAndClient(
        result.type || result.action || '',
        result.funding || {},
        result.client || result.selectedClient || {},
        result.whiteLabelId || this.controls.whiteLabelId.value
      );
      return;
    }

    this.fromApplicationAndClient(
      result.type || result.action || '',
      result.application || {},
      result.client || result.selectedClient || {},
      result.whiteLabelId || this.controls.whiteLabelId.value
    );
  }

  addEmailRecipient(email: string) {
    this.emailRecipients.push(new FormControl(email, [Validators.required, Validators.email]));
    this.emailRecipients.markAsDirty();
    if (this.cdr) {
      this.cdr.markForCheck();
    }
  }

  removeEmailRecipient(index: number) {
    this.emailRecipients.removeAt(index);
    this.emailRecipients.markAsDirty();
    if (this.cdr) {
      this.cdr.markForCheck();
    }
  }
}
